import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Images from "../../utils/Images";
import Config from "../../utils/Config";

const PROVIDERS = [
  {
    value: Config.MTN_PAYMENT,
    label: "MTN Mobile Money",
    brand: "text-yellow-600",
    badge: Images.mtn_momo,
  },
  {
    value: Config.TELECEL_PAYMENT,
    label: "Telecel Cash",
    brand: "text-red-600",
    badge: Images.telecel_cash,
  },
  {
    value: Config.AIRTEL_TIGO_PAYMENT,
    label: "AirtelTigo Money",
    brand: "text-indigo-600",
    badge: Images.at_card,
  },
];

export default function MobileMoneyForm({
  onSubmit,
  defaultProvider = Config.MTN_PAYMENT,
  insuranceProviderSlug = null,
}) {
  const [provider, setProvider] = useState(defaultProvider);
  const [phone, setPhone] = useState("");
  const [touched, setTouched] = useState(false);

  const isValidPhone = useMemo(() => /^0\d{9}$/.test(phone), [phone]);

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-sm font-semibold text-gray-800">
          Choose your Mobile Money provider
        </p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {PROVIDERS.map((p) => (
            <label key={p.value} className="cursor-pointer">
              <input
                type="radio"
                className="sr-only peer"
                name="momo-provider"
                value={p.value}
                checked={provider === p.value}
                onChange={() => setProvider(p.value)}
              />
              <div className="flex gap-2 items-center p-3 bg-white rounded-xl border border-gray-200 peer-checked:border-emerald-400 peer-checked:ring-2 peer-checked:ring-emerald-100 hover:border-emerald-200">
                {p.badge && (
                  <span
                    className={`inline-flex items-center justify-center w-8 h-8 rounded-lg`}
                    style={{ backgroundImage: `url(${p.badge})`, backgroundSize: "cover", backgroundPosition: "center" }}
                  />
                )}
                <span className={`text-sm font-medium ${p.brand}`}>
                  {p.label}
                </span>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-800">
          Mobile Money number
        </label>
        <div className="relative">
          <input
            type="tel"
            inputMode="numeric"
            placeholder="0XXXXXXXXX"
            maxLength={10}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={() => setTouched(true)}
            className={`w-full px-4 py-3 pr-10 rounded-xl border outline-none transition-all ${
              touched && !isValidPhone
                ? "border-red-400 focus:ring-2 focus:ring-red-100"
                : touched && isValidPhone
                ? "border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                : "border-gray-300 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            }`}
          />
          <AnimatePresence>
            {touched && (
              <motion.span
                key="indicator"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                className={`absolute top-1/2 right-3 -translate-y-1/2 inline-flex w-2.5 h-2.5 rounded-full ${
                  isValidPhone ? "bg-emerald-500" : "bg-red-500"
                }`}
              />
            )}
          </AnimatePresence>
          <p className="mt-1 text-xs text-gray-500">
            Enter a 10-digit number starting with 0
          </p>
        </div>
      </div>

      <button
        onClick={() => onSubmit?.({ method: "momo", provider: provider.toUpperCase(), phone, insuranceProviderSlug })}
        disabled={!isValidPhone}
        className="px-4 py-3 w-full font-semibold text-white bg-emerald-600 rounded-xl shadow hover:bg-emerald-700 disabled:opacity-50"
      >
        Pay with Mobile Money
      </button>
    </div>
  );
}
